import { format as fnsFormat, isValid } from "date-fns";

/** Canonical placeholder for a missing/empty date anywhere in the app. */
export const EMPTY_DATE_PLACEHOLDER = "—";

type DateInput = string | number | Date | null | undefined;

const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Parse a plain calendar date ("YYYY-MM-DD") into a local Date anchored at midnight in
 * the *browser's* timezone — never UTC midnight.
 *
 * `new Date("2026-07-06")` parses as UTC midnight, which then renders as the previous day
 * in any timezone behind UTC. Calendar-only fields (DOB, leave dates, invoice due dates, ...)
 * have no time component and no timezone of their own, so they must never go through a
 * UTC-anchored parse or a tenant-timezone conversion.
 */
function parseCalendarDate(str: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(str);
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return isValid(d) ? d : null;
}

function safeIntlFormat(d: Date, opts: Intl.DateTimeFormatOptions, tz: string): string {
  try {
    return new Intl.DateTimeFormat("en-GB", { ...opts, timeZone: tz }).format(d);
  } catch {
    return new Intl.DateTimeFormat("en-GB", { ...opts, timeZone: "UTC" }).format(d);
  }
}

/**
 * Format a date value for display, auto-detecting calendar-only dates ("YYYY-MM-DD", e.g.
 * DOB, leave/invoice dates) vs. instants (full ISO timestamps, e.g. created_at/applied_at).
 * Calendar-only values are never timezone-shifted. Instants are converted to `tz` (the
 * tenant's configured timezone — see `useTenantTimezone`) before their calendar day is
 * determined, so the same instant doesn't render as different days depending on the
 * viewer's own browser timezone.
 */
const FNS_PATTERNS = { compact: "d MMM", short: "d MMM yyyy", long: "EEEE, d MMMM yyyy" } as const;
const INTL_OPTS: Record<"compact" | "short" | "long", Intl.DateTimeFormatOptions> = {
  compact: { day: "numeric", month: "short" },
  short: { day: "numeric", month: "short", year: "numeric" },
  long: { weekday: "long", day: "numeric", month: "long", year: "numeric" },
};

/**
 * Format a date value for display, auto-detecting calendar-only dates ("YYYY-MM-DD", e.g.
 * DOB, leave/invoice dates) vs. instants (full ISO timestamps, e.g. created_at/applied_at).
 * Calendar-only values are never timezone-shifted. Instants are converted to `tz` (the
 * tenant's configured timezone — see `useTenantTimezone`) before their calendar day is
 * determined, so the same instant doesn't render as different days depending on the
 * viewer's own browser timezone.
 *
 * Styles: "short" (default, "6 Jul 2026") for lists/cards/tables; "long" (with weekday) for
 * detail headers; "compact" ("6 Jul", no year) only for space-constrained UI like Kanban cards.
 */
export function formatDate(value: DateInput, style: "compact" | "short" | "long" = "short", tz: string = "UTC"): string {
  if (value == null || value === "") return EMPTY_DATE_PLACEHOLDER;

  if (!(value instanceof Date) && DATE_ONLY_RE.test(String(value))) {
    const d = parseCalendarDate(String(value));
    if (!d) return EMPTY_DATE_PLACEHOLDER;
    return fnsFormat(d, FNS_PATTERNS[style]);
  }

  const d = value instanceof Date ? value : new Date(value);
  if (!isValid(d)) return EMPTY_DATE_PLACEHOLDER;
  return safeIntlFormat(d, INTL_OPTS[style], tz);
}

/**
 * Format an instant (timestamp with a real time component — created_at, applied_at,
 * scheduled_at, etc.) as date + time in the given IANA timezone. Falls back to UTC if
 * `tz` is omitted or unrecognized, rather than silently using the viewer's local timezone.
 */
export function formatDateTime(value: DateInput, tz: string = "UTC"): string {
  if (value == null || value === "") return EMPTY_DATE_PLACEHOLDER;
  const d = value instanceof Date ? value : new Date(value);
  if (!isValid(d)) return EMPTY_DATE_PLACEHOLDER;
  return safeIntlFormat(
    d,
    { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" },
    tz
  );
}

/** Today's date as a "YYYY-MM-DD" string, suitable for a `min`/`max`/`value` on `<input type="date">`. */
export function todayStr(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/**
 * The current hour (0-23) in the tenant's timezone — for "good morning/afternoon/evening"
 * style greetings, so the bucket reflects the tenant's local time of day, not the viewing
 * browser's.
 */
export function tenantHour(tz: string = "UTC"): number {
  try {
    return Number(new Intl.DateTimeFormat("en-US", { hour: "numeric", hourCycle: "h23", timeZone: tz }).format(new Date()));
  } catch {
    return new Date().getUTCHours();
  }
}
